import { css, jsx } from '@emotion/core';
import { Collapse, Pagination, Select, Skeleton } from 'antd';
import moment from 'moment';
import { useRouter } from 'next/router';
import React, { useEffect, useState } from 'react';

import Container from '@/src/shared/components/Container';
import Form from '@/src/shared/components/Form';
import Head from '@/src/shared/components/Head';
import Heading from '@/src/shared/components/Heading';
import Hero from '@/src/shared/components/Hero';
import Layout from '@/src/shared/components/Layout';
import ResultCard from '@/src/shared/components/ResultCard';
import Section from '@/src/shared/components/Section';
import WebLogImage from '@/src/shared/components/WebLogImage';
import { space } from '@/styles';
import formatURL from '@/utils/formatURL';
import searchNewsReleases from '@/utils/searchNewsReleases';
import { getPath } from '@/utils/utils';

const style = {
  select: css`
    max-width: 50%;
    margin-bottom: 8px;
  `,
  collapse: css`
    ${space.m.t20};
    background-color: transparent !important;
    .ant-collapse-header {
      background-color: white !important;
    }
    .ant-collapse-content {
      background-color: transparent !important;
    }
  `,
};

function alphabeticalSort(a, b) {
  const aVal = a[0].toLowerCase();
  const bVal = b[0].toLowerCase();
  if (aVal < bVal) return -1;
  if (aVal > bVal) return 1;
  return 0;
}

const stateSortMap = {
  'All States': 1,
  'Multiple States': 2,
  Federal: 3,
  International: 4,
  NAIC: 5,
  undefined: 6,
  'Not Assigned': 7,
};
function stateCatSort(a, b) {
  const aVal = stateSortMap[a[0]] || 5;
  const bVal = stateSortMap[b[0]] || 5;
  if (aVal < bVal) return -1;
  if (aVal > bVal) return 1;
  return 0;
}
const subjectSortMap = {
  undefined: 0,
  'Not Assigned': 1,
};
function subjectSort(a, b) {
  const aVal = subjectSortMap[a[0]] || 0;
  const bVal = subjectSortMap[b[0]] || 0;
  if (aVal < bVal) return -1;
  if (aVal > bVal) return 1;
  return 0;
}

const PAGE_SIZE = 20;

const NewsRelease = () => {
  const router = useRouter();
  const [results, setResults] = useState([]);
  const [loadingResults, setLoadingResults] = useState(true);
  const [sortOrderState, setSortOrderState] = useState(
    router.query.sortorder && ['date', 'state', 'subject'].includes(router.query.sortorder)
      ? router.query.sortorder
      : 'date'
  );
  const [current, setCurrent] = useState(
    router.query.page && !Number.isNaN(router.query.page) ? Number(router.query.page) : 1
  );

  function handleSelectChange(value) {
    if (value === sortOrderState) return;

    try {
      setLoadingResults(true);
      setCurrent(1);
      const newURL = formatURL(router.asPath, { sortorder: value });
      router.replace(newURL, newURL, { shallow: true });
      setTimeout(() => setLoadingResults(false), 300);
    } catch (error) {
      setTimeout(() => setLoadingResults(false), 150);
    }
  }

  function handlePaginationChange(page) {
    setCurrent(page);
    const newURL = formatURL(router.asPath, { page });
    router.replace(newURL, newURL, { shallow: true });
  }

  function renderResults() {
    const paginatedResults = results.slice((current - 1) * PAGE_SIZE, current * PAGE_SIZE);
    switch (sortOrderState) {
      case 'date': {
        const dateResults = {};
        for (let i = 0; i < paginatedResults.length; i += 1) {
          const result = paginatedResults[i];
          const resultDate = moment(result.PublishDate).format('MMMM YYYY');
          const [as, href] = getPath(result.Cat1Id, result.SitePageId);
          result.As = as;
          result.Href = href;
          if (resultDate in dateResults) {
            dateResults[resultDate].push(result);
          } else {
            dateResults[resultDate] = [result];
          }
        }
        return Object.entries(dateResults).map(([date, _results]) => {
          return (
            <>
              <Heading type={3} weight="bold" theme="primaryGray" css={[space.m.t20, space.m.b20]}>
                {date}
              </Heading>
              {_results.map(result => (
                <ResultCard
                  key={result.SitePageId}
                  as={result.As}
                  href={result.Href}
                  title={result.PageTitle}
                  subtitle={new Date(result.PublishDate).toLocaleDateString()}
                  summary={result.ExecutiveSummary}
                  iconVisible={false}
                />
              ))}
            </>
          );
        });
      }
      case 'state': {
        const stateResults = {};
        for (let i = 0; i < results.length; i += 1) {
          const result = results[i];
          const resultState = result.State;
          const [as, href] = getPath(result.Cat1Id, result.SitePageId);
          result.As = as;
          result.Href = href;
          if (resultState in stateResults) {
            stateResults[resultState].push(result);
          } else {
            stateResults[resultState] = [result];
          }
        }
        return (
          <Collapse accordion css={style.collapse}>
            {Object.entries(stateResults)
              .sort(alphabeticalSort)
              .sort(stateCatSort)
              .map(([state, _results]) => {
                return (
                  <Collapse.Panel header={state}>
                    {_results.map(result => (
                      <ResultCard
                        key={result.SitePageId}
                        as={result.As}
                        href={result.Href}
                        title={result.PageTitle}
                        subtitle={new Date(result.PublishDate).toLocaleDateString()}
                        summary={result.ExecutiveSummary}
                        iconId={result.IconId}
                        iconVisible={false}
                      />
                    ))}
                  </Collapse.Panel>
                );
              })}
          </Collapse>
        );
      }
      case 'subject': {
        const subjectResults = {};
        for (let i = 0; i < results.length; i += 1) {
          const result = results[i];
          const resultState = result.Subject;
          const [as, href] = getPath(result.Cat1Id, result.SitePageId);
          result.As = as;
          result.Href = href;
          if (resultState in subjectResults) {
            subjectResults[resultState].push(result);
          } else {
            subjectResults[resultState] = [result];
          }
        }
        return (
          <Collapse accordion css={style.collapse}>
            {Object.entries(subjectResults)
              .sort(alphabeticalSort)
              .sort(subjectSort)
              .map(([subject, _results]) => {
                return (
                  <Collapse.Panel header={subject}>
                    {_results.map(result => (
                      <ResultCard
                        key={result.SitePageId}
                        as={result.As}
                        href={result.Href}
                        title={result.PageTitle}
                        subtitle={new Date(result.PublishDate).toLocaleDateString()}
                        summary={result.ExecutiveSummary}
                        iconId={result.IconId}
                        iconVisible={false}
                      />
                    ))}
                  </Collapse.Panel>
                );
              })}
          </Collapse>
        );
        // // Sort Not Assigned Last
        // if (stateResults['Not Assigned']) {
        //   const _notAssigned = stateResults['Not Assigned'];
        //   delete stateResults['Not Assigned'];
        //   stateResults['Not Assigned'] = _notAssigned;
        // }
      }
      default:
        return paginatedResults.map(result => {
          const [as, href] = getPath(result.Cat1Id, result.SitePageId);
          return (
            <ResultCard
              key={result.SitePageId}
              as={as}
              href={href}
              title={result.PageTitle}
              subtitle={new Date(result.PublishDate).toLocaleDateString()}
              summary={result.ExecutiveSummary}
              iconId={result.IconId}
              iconVisible={false}
            />
          );
        });
    }
  }

  useEffect(() => {
    (async function mounted() {
      try {
        setLoadingResults(true);
        const response = await searchNewsReleases();
        setResults(response.results);
        setLoadingResults(false);
      } catch (error) {
        setResults([]);
        setLoadingResults(false);
      }
    })();
  }, []);

  useEffect(() => {
    const { sortorder } = router.query;
    if (sortorder && ['date', 'state', 'subject'].includes(sortorder)) {
      setSortOrderState(sortorder);
    }
  }, [router.query]);

  const pageTitle = 'News Releases';

  return (
    <Layout>
      <Head title={pageTitle} />
      <Hero title={pageTitle} />
      <Section bgColor="gray">
        <Container size="sm">
          <Form theme="white">
            <Form.Item label="Sort by" colon>
              <Select
                defaultValue={sortOrderState}
                onChange={handleSelectChange}
                css={style.select}
              >
                <Select.Option value="date">Date</Select.Option>
                <Select.Option value="state">State</Select.Option>
                <Select.Option value="subject">Subject</Select.Option>
              </Select>
            </Form.Item>
          </Form>
          {sortOrderState === 'date' ? (
            <Pagination
              onChange={handlePaginationChange}
              total={results.length}
              pageSize={PAGE_SIZE}
              current={current}
            />
          ) : null}
          {loadingResults ? (
            <>
              <Skeleton />
              <Skeleton />
              <Skeleton />
            </>
          ) : (
            renderResults()
          )}
        </Container>
      </Section>
      <WebLogImage browserPath title="News Releases" />
    </Layout>
  );
};

export default NewsRelease;
